import { components } from "@/lib/ApiSchema";
import { getUrl } from "@/lib/useToPage";
import { useParams } from "react-router";
import QuantityInParent from "./QuantityInParent";
import { useState } from "react";
import toast from "react-hot-toast";
import Select from "react-select";
import useModal from "@/lib/modal/useModal";
import Button from "@/lib/Button";
import useUpdatePart from "@/lib/queries/useUpdatePart";
import ConfirmationModal from "@/lib/modal/ConfirmationModal";
import StyledLink from "@/lib/StyledLink";
import useGetShopSettings from "@/lib/queries/useGetShopSettings";
import useUpdateShopSettings from "@/lib/queries/useUpdateShopSettings";
import { useQueryClient } from "@tanstack/react-query";
import Switch from "@/lib/Switch";
import { Tooltip } from "react-tooltip";

export default function Header({
  part,
}: {
  part: components["schemas"]["RequestedPartWithOperationsSchema"];
}) {
  const { showModal, closeModal } = useModal();
  const { teamSlug, rfqId: rfqIdParam } = useParams();
  const parentLink = getUrl("part", {
    teamSlug: teamSlug!,
    rfqId: rfqIdParam!,
    partId: part.parent?.id ?? "",
  });

  const { updatePart, isPending } = useUpdatePart();

  const options: {
    label: string;
    value: components["schemas"]["NamedWorkflowProcess"];
  }[] = [
    { label: "CNC Milling", value: "CNC_Milling" },
    { label: "CNC Turning", value: "CNC_Turning" },
    { label: "Sheet Metal", value: "Sheet" },
    { label: "Hardware", value: "OTS" },
    { label: "Assembly", value: "Assembly" },
    { label: "Part+Hardware", value: "Part_With_Hardware" },
    { label: "No Operations", value: "Empty" },
  ];

  const { data: shop } = useGetShopSettings(teamSlug);
  const updateShop = useUpdateShopSettings();
  const queryClient = useQueryClient();
  const lengthDisplayUnit = shop?.length_display_unit ?? "in";

  return (
    <div className="flex justify-between items-center">
      <div>
        <div className="text-slate-500 text-xs">Part</div>
        <div className="flex items-baseline gap-2">
          <div className="font-bold">
            {part.part_number} <span className="text-slate-500">Rev</span>{" "}
            {part.revision_number}
          </div>
          {part.parent && (
            <>
              <QuantityInParent part={part} />
              <div className="text-sm">per</div>
              <StyledLink to={parentLink}>{part.parent.part_number}</StyledLink>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          value={lengthDisplayUnit === "mm"}
          label={lengthDisplayUnit}
          onChange={(isMetric) => {
            updateShop(
              {
                params: { path: { team_slug: teamSlug! } },
                body: {
                  length_display_unit: isMetric ? "mm" : "in",
                },
              },
              {
                onSuccess: () => {
                  // update general part info units
                  queryClient.invalidateQueries({
                    queryKey: [
                      "get",
                      "/api/teams/{team_slug}/requested_parts/{id}/extracted_info",
                      {
                        params: {
                          path: {
                            team_slug: teamSlug,
                            id: part.id,
                          },
                        },
                      },
                    ],
                  });
                },
              },
            );
          }}
          data-tooltip-id="length-display-unit-toggle"
          data-tooltip-content="Toggle between inches and millimeters"
          data-tooltip-delay-show={500}
        />
        <Tooltip id="length-display-unit-toggle" />

        <Button
          onClick={() => showModal(<Note done={closeModal} note={part.note} />)}
        >
          Add Note
        </Button>
        <Select
          classNames={{ container: () => "z-50 min-w-36" }}
          options={options}
          isDisabled={part.will_bid !== null}
          isLoading={isPending}
          value={{
            label: options.find((o) => o.value === part.selected_process)
              ?.label,
            value: part.selected_process,
          }}
          onChange={(option) => {
            if (!option || option.value === part.selected_process) return;

            showModal(
              <ConfirmationModal
                onCancel={closeModal}
                onConfirm={() => {
                  updatePart({
                    partId: part.id,
                    payload: {
                      selected_process: option.value,
                    },
                  });
                  closeModal();
                }}
              >
                Changes made to the current
                <br /> estimate will be lost
              </ConfirmationModal>,
            );
          }}
        />
      </div>
    </div>
  );
}

function Note({ done, note }: { done: () => void; note: string }) {
  const [input, setInput] = useState(note);
  const { updatePart, isPending } = useUpdatePart();

  return (
    <div className="w-150 h-150 max-h-10/11 flex flex-col gap-2 p-4 bg-white">
      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Add part specific note to quote"
        className="p-2 w-full grow rounded-md"
      />
      <div className="w-full flex justify-end gap-2 items-center">
        <Button onClick={done}>Cancel</Button>
        <Button
          primary
          loading={isPending}
          onClick={() => {
            updatePart({
              payload: { note: input },
              onSuccess: () => {
                toast.success("Note saved");
                done();
              },
            });
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
